import { Show } from "shared/components";
import moment from "moment";
import { unitStatus } from "shared/app/UnitsTableView/constants";
import { RentAmount } from "shared/components/RentAmount";
import { useAuthContext } from "providers/Auth";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Unit } from "types/landlord/address";
import { getSourceLabel } from "utils/getSourceLabel";
import { AiLeasingAgentField } from "./AiLeasingAgentField";

interface HeaderDesktopDetailsProps {
  unit?: Unit;
  onGoToListUnit: () => void;
}

export const HeaderDesktopDetails = ({
  unit,
  onGoToListUnit
}: HeaderDesktopDetailsProps) => {
  const { pms, type } = useAuthContext();
  const dictionary = useUnitsPropertiesDictionary();

  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  return (
    <div className="flex items-start gap-2 md:gap-12 px-3 md:px-6 py-4 flex-wrap md:flex-nowrap">
      <div>
        <p className="m-0 text-sm font-normal text-content-400">
          Current Status
        </p>
        <p className="m-0 text-sm font-medium capitalize text-content-1000">
          {unitStatus(Boolean(unit?.occupied))}
          {!unit?.pms_occupied_override &&
          unit?.pms_occupied !== unit?.occupied ? (
            <span className="text-content-200"> (Manually overridden)</span>
          ) : null}
        </p>
      </div>

      <div>
        <p className="m-0 text-sm font-normal text-content-400">
          {homeSiteRentEnabled ? "Total rent" : "Monthly Rent"}
        </p>
        <RentAmount
          value={unit?.monthly_rent_amount}
          title={homeSiteRentEnabled ? "Total rent" : "Monthly Rent"}
          className="m-0 text-sm"
          isManuallyOverridden={
            !unit?.pms_monthly_rent_amount_override &&
            unit?.pms_monthly_rent_amount?.cents !==
              unit?.monthly_rent_amount?.cents
          }
        />
      </div>

      <Show if={homeSiteRentEnabled}>
        <div>
          <p className="m-0 text-sm font-normal text-content-400">Home rent</p>
          <RentAmount
            value={unit?.home_rent_amount}
            className="m-0 text-sm"
            isManuallyOverridden={
              !unit?.pms_home_rent_amount_override &&
              unit?.pms_home_rent_amount?.cents !==
                unit?.home_rent_amount?.cents
            }
          />
        </div>
        <div>
          <p className="m-0 text-sm font-normal text-content-400">Site rent</p>
          <RentAmount
            value={unit?.site_rent_amount}
            className="m-0 text-sm"
            isManuallyOverridden={
              !unit?.pms_site_rent_amount_override &&
              unit?.pms_site_rent_amount?.cents !==
                unit?.site_rent_amount?.cents
            }
          />
        </div>
      </Show>

      <div>
        <p className="m-0 text-sm font-normal text-content-400">Available</p>
        <p className="m-0 text-sm font-medium text-content-1000">
          {unit?.available_at
            ? moment(unit.available_at).utc().format("MMM D, YYYY")
            : "--"}
          {!unit?.pms_available_at_override &&
          unit?.pms_available_at !== unit?.available_at ? (
            <span className="text-content-200"> (Manually overridden)</span>
          ) : null}
        </p>
      </div>

      <div>
        <p className="m-0 text-sm font-normal text-content-400">Source</p>
        <p className="m-0 text-sm font-medium text-content-1000">
          {getSourceLabel(unit)}
        </p>
      </div>

      <div>
        <p className="m-0 text-sm font-normal text-content-400">Owner</p>
        <p className="m-0 text-sm font-medium text-content-1000">
          {unit?.property.owner || "--"}
        </p>
      </div>

      <div>
        <p className="m-0 text-sm font-normal text-content-400">
          {dictionary.Unit} ID
        </p>
        <p className="m-0 text-sm font-medium text-content-1000">
          {unit?.external_id || "--"}
        </p>
      </div>

      {unit?.ai_leasing_agent ? (
        <AiLeasingAgentField
          visibility={unit.ai_leasing_agent}
          propertyName={unit.property.name}
          unitListed={Boolean(unit.listed)}
          onGoToListUnit={onGoToListUnit}
        />
      ) : null}
    </div>
  );
};
